// client/src/pages/InfluencerRegisterPage.js
// 인플루언서 가입 — v5 리디자인: 단계 카드 4장 (기본 정보 → 계정 → 주소 → SNS) + 약관
// 검증 순서·인증 플로·제출·업로드 재시도 로직은 기존 그대로. (카드 순서 = 검증 순서)
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import '../styles/pages/EmailcheckModal.css';
import '../styles/public/pub.css';
import PhoneVerification from '../components/PhoneVerification';
import EmailVerification from '../components/EmailVerification';
import IDVerification from '../components/IDVerification';
import AddressSearch from '../components/AddressSearch';
import SNSAccounts from '../components/SNSAccounts';
import RegisterCheckbox from '../components/RegisterCheckbox';
import CertiUploader from '../components/CertiUploader';

const API_URL = process.env.REACT_APP_API_URL;

function InfluencerRegisterPage() {
    const navigate = useNavigate();

    // 성공 모달 상태
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    // 직접 포커스 가능한 인풋 ref
    const nameRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmPasswordRef = useRef(null);

    // 섹션 스크롤 헬퍼
    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    //회원가입 제출하는 DB 목록
    const [formData, setFormData] = useState({
      userName: '',
      userId: '',
      userPassword: '',
      userAddress: '',
      phoneNumber: '',
      email: '',
      marketingOption: false,
      postcode: '',             // 우편번호
      address: '',              // 기본주소
      detailAddress: '',         // 상세주소
      instagramId: '',
      blogName: '',
      youtubeChannel: '',
      tiktokName: '',
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [fieldErrors, setFieldErrors] = useState({}); // 필드별 인라인 에러

    // 특정 필드 에러 설정 + 스크롤/포커스
    const setFieldError = (key, msg, action) => {
        setFieldErrors(prev => ({ ...prev, [key]: msg }));
        if (action) action();
    };

    // 모든 필드 에러 초기화
    const clearFieldErrors = () => setFieldErrors({});

    const [idVerified, setIdVerified] = useState(false); // ID 입력 잠금 상태
    const [emailVerified, setEmailVerified] = useState(false); // 이메일 인증 상태
    const [phoneVerified, setPhoneVerified] = useState(false); // 핸드폰 인증 상태
    const [requiredAgreed, setRequiredAgreed] = useState(false); // 동의 상태
    const [snsCertiFiles, setSnsCertiFiles] = useState([]); // sns 증명 업로드 상태

    // 비밀번호 확인 입력값
    const [confirmPassword, setConfirmPassword] = useState('');

    // 비밀번호 규칙: 영문/숫자/특수기호로 이루어진 8~20자(공백 불가)
    const PASS_RE = /^[A-Za-z0-9~`!@#$%^&*()_\-+={}[\]|\\:;"'<>,.?/]{8,20}$/;
    const isValidPassword = (pw) => PASS_RE.test(String(pw));


    // 입력값 변경 — 해당 필드 에러 자동 제거
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setFieldErrors(prev => ({ ...prev, [e.target.name]: '' }));
    };

    // SNS 증명 업로드 실패 시 재시도 로직을 담은 헬퍼 함수 ------------------------------------
    // fd: FormData(업로드할 파일과 ownerId가 들어있음)
    // url: 업로드 API 주소(`${API_URL}/api/influencer/sns-certi/upload`)
    // 옵션:
    //  - max: 최대 재시도 횟수(기본 3회)
    //  - baseDelay: 첫 대기시간(ms). 0.8초 → 1.6초 → 3.2초처럼 점점 늘어남(지수 백오프)
    //  - timeoutMs: 한 번의 요청이 얼마나 오래 기다릴지(기본 15초)
    const uploadWithRetry = async (fd, url, { max = 3, baseDelay = 800, timeoutMs = 15000 } = {}) => {
        for (let i = 0; i < max; i++) { // 최대 max번 반복
        const ac = new AbortController(); // 요청을 강제로 중단(abort)하기 위한 컨트롤러
        const timer = setTimeout(() => ac.abort(), timeoutMs); // timeoutMs가 지나면 요청 중단

        try {
            // fetch로 파일 업로드. signal을 전달해서 타임아웃 시 abort되도록 함
            const res = await fetch(url, { method: 'POST', body: fd, signal: ac.signal });
            clearTimeout(timer); // 응답을 받았으니 타이머 해제
            if (res.ok) return true; // 200~299면 성공 → 함수 종료
        } catch (e) {
            // 네트워크 끊김/타임아웃/일시 오류 등은 여기로 들어옴 → 다음 루프에서 재시도
        }

        // 지수 백오프: 0.8s, 1.6s, 3.2s... + 약간의 랜덤 지연(동시 재시도 충돌 완화)
        const delay = baseDelay * (2 ** i) + Math.random() * 300;
        await new Promise(r => setTimeout(r, delay)); // delay 만큼 기다림
        }
        return false; // max번 모두 실패하면 false 반환(호출 측에서 후속 처리)
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        clearFieldErrors();

        // ── 필드별 검증 (페이지 순서대로) ──────────────────────
        if (!formData.userName.trim()) {
            setFieldError('userName', '이름을 입력해주세요.', () => nameRef.current?.focus());
            return;
        }
        if (!formData.phoneNumber.trim()) {
            setFieldError('phone', '휴대폰 번호를 입력해주세요.', () => scrollTo('section-phone'));
            return;
        }
        if (!phoneVerified) {
            setFieldError('phone', '휴대폰 인증을 완료해주세요.', () => scrollTo('section-phone'));
            return;
        }
        if (!formData.email.trim()) {
            setFieldError('email', '이메일을 입력해주세요.', () => scrollTo('section-email'));
            return;
        }
        if (!emailVerified) {
            setFieldError('email', '이메일 인증을 완료해주세요.', () => scrollTo('section-email'));
            return;
        }
        if (!formData.userId.trim()) {
            setFieldError('userId', '아이디를 입력해주세요.', () => scrollTo('section-id'));
            return;
        }
        if (!idVerified) {
            setFieldError('userId', 'ID 중복검사를 완료해주세요.', () => scrollTo('section-id'));
            return;
        }
        if (!formData.userPassword.trim()) {
            setFieldError('userPassword', '비밀번호를 입력해주세요.', () => passwordRef.current?.focus());
            return;
        }
        if (!isValidPassword(formData.userPassword)) {
            setFieldError('userPassword', '영문/숫자/특수기호 조합 8~20자, 공백 불가', () => passwordRef.current?.focus());
            return;
        }
        if (formData.userPassword !== confirmPassword) {
            setFieldError('confirmPassword', '비밀번호가 일치하지 않습니다.', () => confirmPasswordRef.current?.focus());
            return;
        }
        if (!formData.postcode.trim() || !formData.address.trim() || !formData.detailAddress.trim()) {
            setFieldError('address', '주소를 모두 입력해주세요.', () => scrollTo('section-address'));
            return;
        }
        if (!requiredAgreed) {
            setFieldError('terms', '필수 약관에 동의해주세요.', () => scrollTo('section-terms'));
            return;
        }
        setLoading(true);
        setMessage('');

        try {
            const finalAddress = `${formData.postcode} ${formData.address} ${formData.detailAddress}`.trim();
            const snsInfo = [
                formData.instagramId && { platform: 'instagram', accountName: formData.instagramId },
                formData.blogName && { platform: 'blog', accountName: formData.blogName },
                formData.youtubeChannel && { platform: 'youtube', accountName: formData.youtubeChannel },
                formData.tiktokName && { platform: 'tiktok', accountName: formData.tiktokName },
            ].filter(Boolean);
            const response = await fetch(`${API_URL}/api/influencer/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userName: formData.userName,
                userId: formData.userId,
                userPassword: formData.userPassword,
                userAddress: finalAddress,
                snsInfo,
                phoneNumber: formData.phoneNumber,
                email: formData.email,
                marketingOption: formData.marketingOption,
                registeredAt: new Date().toISOString(), // ISO 포맷 추천
            })
            });

            const data = await response.json();

            if (response.ok) {
            if (typeof window.fbq === 'function') {
              window.fbq('track', 'CompleteRegistration', { content_name: 'influencer' });
            }
            setIsSuccessModalOpen(true); // 성공 모달 표시

            const toUpload = [...snsCertiFiles];
            setSnsCertiFiles([]); // 즉시 목록 초기화

            if (toUpload.length) {
              const fd = new FormData();
              fd.append('ownerId', data.user.id);
              toUpload.forEach(f => fd.append('files', f));
              uploadWithRetry(fd, `${API_URL}/api/influencer/sns-certi/upload`, {
                max: 3,          // 최대 3회
                baseDelay: 800,  // 0.8s, 1.6s, 3.2s...
                timeoutMs: 15000 // 요청 타임아웃 15초
              }).then(ok => {
                if (!ok) console.warn('증빙 이미지 업로드 재시도 후 실패');
              });
            }


            // 2) 업로드 결과를 기다리지 않고 즉시 폼 초기화/후속처리
            setFormData({userName: '', userId: '', userPassword: '', userAddress: '', phoneNumber: '', email: '', marketingOption: false, postcode: '', address: '', detailAddress: '',
                instagramId: '', blogName: '', youtubeChannel: '', tiktokName: ''});
            setConfirmPassword('');
            setIdVerified(false);
            setEmailVerified(false);
            setPhoneVerified(false);
            setRequiredAgreed(false);
            clearFieldErrors();
            } else {
            setMessage(`회원가입 오류입니다: ${data.message}`);
            }
        } catch (err) {
            setMessage('서버 요청 실패');
        }
        setLoading(false);
        };

    const fieldError = (key) =>
        fieldErrors[key] ? (
            <span style={{ display: 'block', fontSize: 12, color: '#d13b3b', marginTop: 6, fontWeight: 600 }}>
                {fieldErrors[key]}
            </span>
        ) : null;

    return (
        <div className="pub">
            <Header />

            {/* ── 회원가입 성공 모달 ── */}
            {isSuccessModalOpen && (
                <div className="pub-modal">
                    <div className="pub-modal__box">
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#e7f7f0', color: '#0a8f62', display: 'grid', placeItems: 'center', fontSize: 24, fontWeight: 800, margin: '0 auto 14px' }}>✓</div>
                        <p style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>회원가입 요청 완료</p>
                        <p>
                            회원가입 요청이 완료되었습니다.<br />
                            운영팀이 확인 후, 영업일 1일 이내<br />
                            회원가입 승인을 완료합니다.
                        </p>
                        <button
                            type="button"
                            className="btn btn--mint btn--full"
                            onClick={() => navigate('/')}
                        >
                            확인
                        </button>
                    </div>
                </div>
            )}

            <main className="wrap wrap--narrow">
                <div className="pub-head">
                    <h1>인플루언서 가입</h1>
                    <p>본인 인증과 SNS 계정만 있으면 바로 시작할 수 있어요.</p>
                </div>

                {/* 서버 에러 메시지 */}
                {message && <div className="note note--red" style={{ marginTop: 0, marginBottom: 14 }}>{message}</div>}

                {/* 폼 */}
                <form onSubmit={handleSubmit}>
                    {/* ── 1. 기본 정보 (본인 확인) ── */}
                    <div className="card">
                        <div className="reg-step is-mint"><i>1</i><b>본인 확인</b></div>

                        <div className="field">
                            <label>이름<span className="req">*</span></label>
                            <input
                                ref={nameRef}
                                name="userName"
                                placeholder="실명을 입력하세요 — 환전 통장 예금주와 같아야 해요"
                                value={formData.userName}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                style={fieldErrors.userName ? { borderColor: '#d13b3b' } : undefined}
                            />
                            {fieldError('userName')}
                        </div>

                        <div id="section-phone" className="field">
                            <label>휴대폰 번호<span className="req">*</span></label>
                            <PhoneVerification formData={formData} setFormData={setFormData} phoneVerified={phoneVerified} setPhoneVerified={setPhoneVerified} />
                            {fieldError('phone')}
                        </div>

                        <div id="section-email" className="field" style={{ marginBottom: 0 }}>
                            <label>이메일<span className="req">*</span></label>
                            <EmailVerification formData={formData} setFormData={setFormData} emailVerified={emailVerified} setEmailVerified={setEmailVerified} />
                            {fieldError('email')}
                        </div>
                    </div>

                    {/* ── 2. 계정 만들기 ── */}
                    <div className="card">
                        <div className="reg-step is-mint"><i>2</i><b>계정 만들기</b></div>

                        <div id="section-id" className="field">
                            <label>아이디<span className="req">*</span></label>
                            <IDVerification formData={formData} setFormData={setFormData} idVerified={idVerified} setIdVerified={setIdVerified} loading={loading} />
                            {fieldError('userId')}
                        </div>

                        <div className="grid2">
                            <div className="field" style={{ margin: 0 }}>
                                <label>비밀번호<span className="req">*</span></label>
                                <input
                                    ref={passwordRef}
                                    name="userPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="영문/숫자/특수기호 8~20자"
                                    value={formData.userPassword}
                                    onChange={handleChange}
                                    required
                                    disabled={loading}
                                    minLength={8}
                                    maxLength={20}
                                    style={fieldErrors.userPassword ? { borderColor: '#d13b3b' } : undefined}
                                />
                                {fieldError('userPassword')}
                                {!fieldErrors.userPassword && formData.userPassword && !isValidPassword(formData.userPassword) && (
                                    <span style={{ display: 'block', fontSize: 12, color: '#d13b3b', marginTop: 6, fontWeight: 600 }}>
                                        영문/숫자(선택)/특수기호(선택) 조합 8~20자, 공백 불가
                                    </span>
                                )}
                            </div>
                            <div className="field" style={{ margin: 0 }}>
                                <label>비밀번호 확인<span className="req">*</span></label>
                                <input
                                    ref={confirmPasswordRef}
                                    name="confirmPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    placeholder="한 번 더 입력"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
                                    }}
                                    required
                                    disabled={loading}
                                    style={fieldErrors.confirmPassword ? { borderColor: '#d13b3b' } : undefined}
                                />
                                {fieldError('confirmPassword')}
                                {!fieldErrors.confirmPassword && confirmPassword && formData.userPassword !== confirmPassword && (
                                    <span style={{ display: 'block', fontSize: 12, color: '#d13b3b', marginTop: 6, fontWeight: 600 }}>
                                        비밀번호가 일치하지 않습니다.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── 3. 배송지 주소 ── */}
                    <div id="section-address" className="card">
                        <div className="reg-step is-mint"><i>3</i><b>배송지 주소</b></div>
                        <AddressSearch formData={formData} setFormData={setFormData} loading={loading} />
                        {fieldError('address')}
                        <p className="hint" style={{ fontSize: 12, color: '#6b7686', marginTop: 8 }}>배송형 프로젝트의 제품이 이 주소로 배송돼요.</p>
                    </div>

                    {/* ── 4. SNS 계정 등록 ── */}
                    <div className="card">
                        <div className="reg-step is-mint"><i>4</i><b>SNS 계정 등록</b></div>
                        <SNSAccounts formData={formData} setFormData={setFormData} loading={loading} />
                        <div style={{ marginTop: 14 }}>
                            <CertiUploader files={snsCertiFiles} setFiles={setSnsCertiFiles} loading={loading} label="해당 계정을 소유함을 증명할 수 있는 이미지를 첨부해주세요. (캡처 이미지 가능)" />
                        </div>
                    </div>

                    {/* ── 약관 동의 + 제출 ── */}
                    <div className="card">
                        <div id="section-terms">
                            <RegisterCheckbox requiredAgreed={requiredAgreed} setRequiredAgreed={setRequiredAgreed} formData={formData} setFormData={setFormData} loading={loading} />
                            {fieldError('terms')}
                        </div>

                        <button className="btn btn--mint btn--full btn--lg" type="submit" disabled={loading} style={{ marginTop: 10 }}>
                            {loading ? '처리 중...' : '가입 신청하기'}
                        </button>
                        <p className="hint" style={{ fontSize: 12, color: '#6b7686', marginTop: 10, textAlign: 'center' }}>
                            계정 검증(허위 팔로워 확인)이 끝나면 활동을 시작할 수 있어요.
                        </p>
                    </div>
                </form>
            </main>
        </div>
      );
}

export default InfluencerRegisterPage;
